import { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// GET /api/terms is public and unauthenticated — you cannot ask someone to accept what they
// cannot read, and the register page has no token yet.
export const fetchTerms = async () => {
  const { data } = await axios.get(`${API_URL}/api/terms`);
  return data;
};

// A deliberately small markdown renderer, rather than a dependency. The document it renders
// is one we ship and control: headings, paragraphs, list items, blockquotes and bold. It
// never renders raw HTML, so the agreement cannot smuggle markup into the page.
const inline = (text, keyBase) =>
  text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyBase}-${i}`} className="font-semibold text-[#1A1A1A]">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={`${keyBase}-${i}`}>{part.slice(1, -1)}</em>;
    }
    return <span key={`${keyBase}-${i}`}>{part}</span>;
  });

const renderMarkdown = (markdown) => {
  const blocks = [];
  let paragraph = [];

  const flush = () => {
    if (!paragraph.length) return;
    const key = `p-${blocks.length}`;
    blocks.push(
      <p key={key} className="text-sm leading-relaxed text-[#64748B]">
        {inline(paragraph.join(' '), key)}
      </p>
    );
    paragraph = [];
  };

  (markdown || '').split('\n').forEach((raw, i) => {
    const line = raw.trimEnd();
    if (!line.trim()) { flush(); return; }
    if (line.startsWith('###')) {
      flush();
      blocks.push(<h3 key={`h-${i}`} className="text-sm font-semibold text-[#1A1A1A] mt-5">{inline(line.replace(/^#+\s*/, ''), `h-${i}`)}</h3>);
    } else if (line.startsWith('##')) {
      flush();
      blocks.push(<h2 key={`h-${i}`} className="text-base font-semibold text-[#1A1A1A] mt-6">{inline(line.replace(/^#+\s*/, ''), `h-${i}`)}</h2>);
    } else if (line.startsWith('#')) {
      flush();
      blocks.push(<h1 key={`h-${i}`} className="text-lg font-bold text-[#1A1A1A]">{inline(line.replace(/^#+\s*/, ''), `h-${i}`)}</h1>);
    } else if (line.startsWith('>')) {
      flush();
      blocks.push(
        <blockquote key={`q-${i}`} className="border-l-2 border-[#ee785b] bg-[#FAFAF8] pl-4 py-2 text-sm text-[#64748B]">
          {inline(line.replace(/^>\s?/, ''), `q-${i}`)}
        </blockquote>
      );
    } else if (/^[-*]\s/.test(line)) {
      flush();
      blocks.push(
        <li key={`li-${i}`} className="ml-5 list-disc text-sm leading-relaxed text-[#64748B]">
          {inline(line.replace(/^[-*]\s/, ''), `li-${i}`)}
        </li>
      );
    } else if (/^\|/.test(line.trim())) {
      // Two-column definition tables, which is the only table shape these documents use — the
      // executed agreement opens with one (party, constitution, address, PAN, signatory). The
      // separator row is markdown plumbing and is dropped rather than rendered as a row of
      // dashes, and an empty header renders as no header rather than as two blank cells.
      flush();
      const cells = line.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      if (cells.every((c) => /^:?-{2,}:?$/.test(c))) return;
      const isHeading = cells.filter(Boolean).length === 0;
      if (isHeading) return;
      blocks.push(
        <div key={`t-${i}`} className="flex gap-4 border-b border-[#E2E2DF] py-2 text-sm last:border-0">
          <span className="w-44 flex-shrink-0 text-[#64748B]">{inline(cells[0], `tk-${i}`)}</span>
          <span className="text-[#1A1A1A]">{inline(cells.slice(1).join(' · '), `tv-${i}`)}</span>
        </div>
      );
    } else if (/^-{3,}$/.test(line.trim())) {
      flush();
      blocks.push(<hr key={`hr-${i}`} className="border-[#E2E2DF] my-4" />);
    } else {
      paragraph.push(line.trim());
    }
  });
  flush();
  return blocks;
};

// Exported so the executed agreement renders through exactly the same code as the terms it
// carries. A second renderer would eventually disagree with this one about the same document.
export { renderMarkdown };

/**
 * The partner agreement, fetched and rendered. Used by the public /terms page, the
 * registration modal and the re-acceptance gate, so all three show the same document from
 * the same source — there is no second copy of the text to drift.
 *
 * `onLoad` receives {version, hash} for callers that want to show which version is on screen.
 */
const TermsDocument = ({ onLoad, className = '' }) => {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchTerms()
      .then((data) => {
        if (cancelled) return;
        setDoc(data);
        if (onLoad) onLoad({ version: data.version, hash: data.hash });
      })
      .catch(() => !cancelled && setError('Could not load the partner terms. Please try again.'));
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <p className={`text-sm text-red-600 ${className}`}>{error}</p>;
  if (!doc) return <p className={`text-sm text-[#64748B] ${className}`}>Loading the terms…</p>;

  return (
    <div className={`space-y-3 ${className}`} data-testid="terms-document">
      {renderMarkdown(doc.content)}
      <p className="pt-4 mt-4 border-t border-[#E2E2DF] text-xs text-[#94A3B8]">
        Version {doc.version} · fingerprint {doc.hash.slice(0, 16)}…
      </p>
    </div>
  );
};

export default TermsDocument;
